import {
	Dialog as BaseDialog,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import type { PropsWithChildren } from "react";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	className?: string;
	titleClassName?: string;
	disabled?: boolean;
	elevated?: boolean;
	layout?: boolean | "position" | "size" | "preserve-aspect";
}

const Dialog = ({
	isOpen,
	onClose,
	title,
	children,
	className,
	titleClassName,
	disabled,
	elevated,
	layout,
}: PropsWithChildren<Props>) => {
	return (
		<AnimatePresence>
			{isOpen && (
				<BaseDialog
					static
					open={isOpen}
					onClose={disabled ? () => {} : onClose}
				>
					<dialog open>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: { duration: 0.3 } }}
							exit={{ opacity: 0, transition: { duration: 0.2 } }}
							className={clsx(
								"fixed inset-0 bg-black/40 dark:bg-black/50",
								elevated ? "z-60" : "z-40",
							)}
							aria-hidden="true"
						/>

						<div
							className={clsx(
								"fixed inset-0 flex w-screen items-start phablet:items-center justify-center overflow-auto phablet:px-8 phablet:py-10 pt-20",
								elevated ? "z-70" : "z-50",
							)}
						>
							<div className="phablet:my-auto mt-auto w-full">
								<DialogPanel
									layout={layout}
									as={motion.div}
									initial={{ opacity: 0, y: 20 }}
									animate={{
										opacity: 1,
										y: 0,
										transition: { duration: 0.2 },
									}}
									exit={{ opacity: 0, y: 20, transition: { duration: 0.15 } }}
									className={clsx(
										"relative mx-auto flex w-full flex-col gap-4 phablet:gap-4 phablet:rounded-2xl rounded-t-2xl border border-transparent phablet:border-b border-b-0 bg-white phablet:px-6 px-4 phablet:pt-4 pt-3.5 pb-8 phablet:pb-6 shadow-2xl shadow-black/10 dark:border-inherit dark:bg-slate-800 dark:shadow-slate-900",
										className,
									)}
								>
									<div className="flex items-center justify-between gap-2">
										<DialogTitle
											className={clsx(
												"line-clamp-1 font-semibold phablet:text-2xl text-xl",
												titleClassName,
											)}
										>
											{title}
										</DialogTitle>
										<button
											disabled={disabled}
											className="relative phablet:-right-1 cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
											onClick={onClose}
										>
											<XIcon className="size-5" strokeWidth={2.2} />
										</button>
									</div>

									{children}
								</DialogPanel>
							</div>
						</div>
					</dialog>
				</BaseDialog>
			)}
		</AnimatePresence>
	);
};

export default Dialog;
